import type { EmailTemplate, LeadSource, PriorityRule } from '../types'

export const leadSources: LeadSource[] = [
  {
    id: 'lendingtree',
    name: 'LendingTree',
    enabled: true,
    monthlyVolume: 940,
    rules: [
      { id: 'lt-1', minDays: 0, maxDays: 1, callsPerDay: 4, enabled: true },
      { id: 'lt-2', minDays: 2, maxDays: 3, callsPerDay: 3, enabled: true },
      { id: 'lt-3', minDays: 4, maxDays: 7, callsPerDay: 2, enabled: true },
      { id: 'lt-4', minDays: 8, maxDays: 30, callsPerDay: 1, enabled: true },
    ],
  },
  {
    id: 'zillow',
    name: 'Zillow',
    enabled: true,
    monthlyVolume: 620,
    rules: [
      { id: 'zl-1', minDays: 0, maxDays: 1, callsPerDay: 4, enabled: true },
      { id: 'zl-2', minDays: 2, maxDays: 5, callsPerDay: 2, enabled: true },
      { id: 'zl-3', minDays: 6, maxDays: 21, callsPerDay: 1, enabled: true },
    ],
  },
  {
    id: 'realtor',
    name: 'Realtor.com',
    enabled: true,
    monthlyVolume: 385,
    rules: [
      { id: 'rl-1', minDays: 0, maxDays: 2, callsPerDay: 3, enabled: true },
      { id: 'rl-2', minDays: 3, maxDays: 10, callsPerDay: 2, enabled: true },
      { id: 'rl-3', minDays: 11, maxDays: 30, callsPerDay: 1, enabled: false },
    ],
  },
  {
    id: 'directmail',
    name: 'Direct Mail',
    enabled: true,
    monthlyVolume: 210,
    rules: [
      { id: 'dm-1', minDays: 0, maxDays: 3, callsPerDay: 2, enabled: true },
      { id: 'dm-2', minDays: 4, maxDays: 14, callsPerDay: 1, enabled: true },
    ],
  },
  {
    id: 'website',
    name: 'Website',
    enabled: true,
    monthlyVolume: 448,
    rules: [
      { id: 'wb-1', minDays: 0, maxDays: 1, callsPerDay: 5, enabled: true },
      { id: 'wb-2', minDays: 2, maxDays: 4, callsPerDay: 3, enabled: true },
      { id: 'wb-3', minDays: 5, maxDays: 20, callsPerDay: 1, enabled: true },
    ],
  },
  {
    id: 'referral',
    name: 'Referral',
    enabled: false,
    monthlyVolume: 96,
    rules: [
      { id: 'rf-1', minDays: 0, maxDays: 2, callsPerDay: 2, enabled: true },
      { id: 'rf-2', minDays: 3, maxDays: 14, callsPerDay: 1, enabled: true },
    ],
  },
]

export const priorityRules: PriorityRule[] = [
  {
    id: 'p-hot',
    label: 'New leads less than 5 minutes old',
    summary:
      'Brand new leads from any active source jump to the front of every queue for the first 5 minutes.',
    enabled: true,
    leadCount: 12,
  },
  {
    id: 'p-callback',
    label: 'Past Due Callbacks',
    summary: 'Past Due Callbacks are shown before standard cadence leads.',
    enabled: true,
    leadCount: 38,
  },
  {
    id: 'p-lt-01',
    label: 'LendingTree — Age 0–1 Days',
    summary:
      'LendingTree leads that are 0–1 days old remain in the LO queue until 4 calls have been completed today.',
    enabled: true,
    leadCount: 64,
  },
  {
    id: 'p-zl-01',
    label: 'Zillow — Age 0–1 Days',
    summary:
      'Zillow leads that are 0–1 days old remain in the LO queue until 4 calls have been completed today.',
    enabled: true,
    leadCount: 41,
  },
  {
    id: 'p-lt-23',
    label: 'LendingTree — Age 2–3 Days',
    summary:
      'LendingTree leads that are 2–3 days old remain in the LO queue until 3 calls have been completed today.',
    enabled: true,
    leadCount: 57,
  },
  {
    id: 'p-followup',
    label: 'Required Follow-Up',
    summary:
      'Leads with an open follow-up task due today are worked after same-day cadence leads.',
    enabled: true,
    leadCount: 73,
  },
  {
    id: 'p-aged',
    label: 'Aged Lead Follow-Up',
    summary:
      'Leads older than 8 days are worked last, once per day, until they are contacted or aged out at 30 days.',
    enabled: true,
    leadCount: 186,
  },
  {
    id: 'p-referral',
    label: 'Referral — Any Age',
    summary: 'Referral leads are currently disabled and will not appear in any LO queue.',
    enabled: false,
    leadCount: 0,
  },
]

export const emailTemplates: EmailTemplate[] = [
  {
    id: 't-intro',
    name: 'New Lead Introduction',
    subject: 'Your mortgage options with Harborline Lending',
    body: "Hi {{FirstName}},\n\nThanks for requesting information about your home financing. I'm your dedicated loan officer and I've reserved time today to walk you through your options and current rates.\n\nWhat's the best number and time to reach you?\n\nBest,\n{{OwnerName}}\nHarborline Lending | NMLS #248113",
  },
  {
    id: 't-missed',
    name: 'Missed Call Follow-Up',
    subject: 'Sorry we missed you, {{FirstName}}',
    body: "Hi {{FirstName}},\n\nI tried reaching you today about your mortgage inquiry. I can share a personalized rate quote in about 10 minutes on the phone — no credit pull required to start.\n\nReply with a good time and I'll call you then.\n\nBest,\n{{OwnerName}}",
  },
  {
    id: 't-rate',
    name: 'Rate Update',
    subject: 'Rates moved — here is what it means for you',
    body: "Hi {{FirstName}},\n\nRates shifted this week and it may change your monthly payment meaningfully. I put together a quick comparison for a loan amount in your range.\n\nWant me to send the numbers over?\n\nBest,\n{{OwnerName}}",
  },
  {
    id: 't-docs',
    name: 'Document Checklist',
    subject: 'Documents to get your pre-approval started',
    body: "Hi {{FirstName}},\n\nHere is the short list to get your pre-approval moving:\n• Last 2 pay stubs\n• Last 2 months of bank statements\n• Photo ID\n\nYou can upload everything securely from the link in your portal.\n\nBest,\n{{OwnerName}}",
  },
]
